import request from '../api/request';
import * as alerts from 'actions/alertActions';
import {formValidation, handleError} from '../helpers';
import {equipmentActionTypes as types} from './actionTypes';
import {Sorting, EquipmentFieldTypes, UserRoles} from '../helpers/constants';
import {asyncLoad, asyncPost, asyncPatch, asyncDelete} from './.';

const getParamsAndFilters = state => {
  const {
    filters,
    pageInfo,
    quickFilter,
    searchString,
    sortInfo,
  } = state.equipment;
  const apiSortOrder = sortInfo.sortOrder === Sorting.desc ? '-' : '';
  const params = `pageSize=${
    pageInfo.pageSize
  }&rowIndex=${(pageInfo.pageNumber - 1) *
    pageInfo.pageSize}&sort=${apiSortOrder}${sortInfo.sortColumn}${
    quickFilter ? `&status=${quickFilter}` : ''
  }${searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''}`;
  let filterParams = '';
  const dateWhitelist = [
    'installDateFrom',
    'installDateTo',
    'lastServiceDateFrom',
    'lastServiceDateTo',
    'nextServiceDueFrom',
    'nextServiceDueTo',
  ];

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(x => {
        filterParams += `&${key}=${encodeURIComponent(x)}`;
      });
    } else if (dateWhitelist.includes(key)) {
      filterParams += `&${key}=${moment(value).format()}`;
    } else if (key !== 'customerId' || (key === 'customerId' && value !== 0)) {
      filterParams += `&${key}=${encodeURIComponent(value)}`;
    }
  });

  return {filterParams, params};
};

const loadEquipmentMaster = ({equipmentId = null, fields}) => (
  dispatch,
  getState,
) => {
  const {filterParams, params} = getParamsAndFilters(getState());

  return asyncLoad({
    dispatch,
    title: 'equipment',
    url: `/equipment${
      equipmentId
        ? `/${equipmentId}${fields ? `?fields=${fields}` : ''}`
        : `?${params}${fields ? `&fields=${fields}` : ''}${filterParams || ''}`
    }`,
  });
};

export const loadEquipmentItem = equipmentId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'equipmentItem',
    url: `/equipment/${equipmentId}`,
  });

export const loadEquipmentActivity = equipmentId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'equipmentActivity',
    url: `/ProductLog?productId=${equipmentId}&skip=0&take=100`,
    params: {useMAPI: true},
  });

export const loadEquipment = () => async dispatch => {
  const response = await dispatch(
    loadEquipmentMaster({
      fields:
        'address,barCode,city,contactId,contactFirstName,contactLastName,customerId,customerName,description,equipmentId,equipmentTypeName,equipmentTypeId,installDate,lastServiceDate,manufacturer,model,name,nextServiceDue,state,serialNumber,serviceLocationId,zip,primaryPhone,secondaryPhone',
    }),
  );

  dispatch({type: types.INITIAL_LOAD, payload: response.totalRows});

  return response;
};

export const changePage = ({pageNumber = 0, size = null} = {}) => (
  dispatch,
  getState,
) => {
  const {pageSize} = getState().equipment.pageInfo;

  dispatch({
    type: types.PAGE_CHANGED,
    payload: {
      pageNumber: pageNumber + 1,
      pageSize: size || pageSize,
    },
  });

  return dispatch(loadEquipment());
};

export const changeSort = ({column = null, order = null}) => (
  dispatch,
  getState,
) => {
  const {sortColumn, sortOrder} = getState().equipment.sortInfo;

  dispatch({
    type: types.SORT_CHANGED,
    payload: {
      sortColumn: column || sortColumn,
      sortOrder: order || sortOrder,
    },
  });

  return dispatch(loadEquipment());
};

export const loadCustomFields = equipmentId => dispatch =>
  dispatch(
    loadEquipmentMaster({
      equipmentId,
      fields: 'customFields{customFieldId,label,sortOrder,description,value}',
    }),
  );

export const loadEquipmentTypes = (includeCustomFields = false) => dispatch =>
  asyncLoad({
    dispatch,
    title: 'equipment types',
    url: `/equipmenttypes?fields=category,equipmentTypeId,parentId${
      includeCustomFields ? ',customFields' : ''
    }`,
  });

export const saveEquipment = equipment => (dispatch, getState) => {
  const formObject = formValidation.setupFormBody(
    equipment,
    equipment.equipmentId !== undefined,
  );
  const equipmentKeyword = getState().portal.dictionary.Asset.value;

  if (formObject.hasError) {
    handleError(formObject.errorMessage, null, dispatch);
    return null;
  }

  if (equipment.equipmentId) {
    if (formObject.body.length === 0) {
      return Promise.resolve(true);
    }
    return asyncPatch({
      body: formObject.body,
      dispatch,
      successMessage: `The ${equipmentKeyword} has been updated successfully.`,
      title: equipmentKeyword,
      url: `/equipment/${equipment.equipmentId.value}`,
    });
  }

  return asyncPost({
    body: formObject.body,
    dispatch,
    successMessage: `The ${equipmentKeyword} has been added successfully.`,
    title: equipmentKeyword,
    url: '/equipment',
  });
};

export const deleteEquipment = equipmentId => (dispatch, getState) => {
  const equipmentKeyword = getState().portal.dictionary.Asset.value;

  return asyncDelete({
    dispatch,
    successMessage: `This ${equipmentKeyword} has been deleted successfully.`,
    title: equipmentKeyword,
    url: `/equipment/${equipmentId}`,
  });
};

export const loadEquipmentTickets = equipmentId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'tickets',
    url: `/equipment/${equipmentId}/tickets?fields=appointments{},assignedTo,assigneeName,ticketStatus,ticketId,ticketNumber,subject,typeName,customerId,serviceLocationid,customer{Name},serviceLocation{Name}`,
  });

export const applyFilters = filters => dispatch => {
  dispatch({type: types.CHANGE_FILTERS, payload: filters});
  dispatch(changePage());
};

export const addTag = (filter, tag) => dispatch =>
  dispatch({
    type: types.ADD_TAG,
    payload: {filter, tag: {text: tag, value: tag}},
  });

export const applyQuickFilter = filter => dispatch => {
  dispatch({type: types.CHANGE_QUICK_FILTER, payload: filter});
  dispatch(changePage());

  return dispatch(loadEquipment());
};

export const search = searchString => dispatch => {
  dispatch({type: types.SEARCH, payload: searchString});
  dispatch(changePage());

  return dispatch(loadEquipment());
};

export const loadServiceLocationEquipment = (
  equipment,
  rowIndex,
) => dispatch => {
  const params = `${
    equipment.serviceLocationId.value === equipment.customerId.value
      ? `CustomerId=${equipment.customerId.value}`
      : `ServiceLocationId=${equipment.serviceLocationId.value}`
  }&rowIndex=${rowIndex}`;

  return asyncLoad({
    dispatch,
    title: 'equipment',
    url: `/equipment?${params}`,
  });
};

export const updateEquipment = (equipment, customFields) => (
  dispatch,
  getState,
) => {
  const equipmentKeyword = getState().portal.dictionary.Asset.value;
  const customerKeyword = getState().portal.dictionary.CustomerName.value;

  if (!equipment.name || !equipment.name.trim()) {
    handleError('Name is a required field', null, dispatch);
    return null;
  }

  const missingCustomFields = customFields.filter(
    x =>
      x.type.isRequired &&
      x.type.controlTypeId !== EquipmentFieldTypes.ReadOnly &&
      (!x.value || (typeof x.value === 'string' && !x.value.trim())) &&
      x.value !== 0,
  );

  missingCustomFields.forEach(x => {
    handleError(
      `${x.type.fieldLabel.replace(/_/g, ' ')} is a required field`,
      null,
      dispatch,
    );
  });

  if (missingCustomFields.length > 0) {
    return null;
  }

  if (equipment.equipmentId) {
    return asyncPost({
      url: `/equipment/${equipment.equipmentId}`,
      title: equipmentKeyword,
      params: {method: 'PUT'},
      body: {
        ...equipment,
        contactId: equipment.contactId === 0 ? null : equipment.contactId,
        customFields: customFields.filter(x => x.value).map(x => ({
          CustomFieldId: x.customFieldId,
          FieldValue: x.value,
        })),
      },
      dispatch,
      successMessage: `The ${equipmentKeyword} has been updated successfully.`,
    });
  }

  if (!equipment.customerId) {
    handleError(`${customerKeyword} is a required field`, null, dispatch);
    return null;
  }

  return asyncPost({
    url: '/equipment',
    title: equipmentKeyword,
    body: {
      ...equipment,
      customFields: customFields.filter(x => x.value).map(x => ({
        CustomFieldId: x.customFieldId,
        FieldValue: x.value,
      })),
    },
    dispatch,
    successMessage: `The ${equipmentKeyword} has been saved successfully.`,
  });
};

export const createTicketForEquipment = (equipment, equipmentItems) => (
  dispatch,
  getState,
) => {
  const {user, portal} = getState();
  const AssignNewTicketToCurrentUser =
    portal.portalSettings.AssignNewTicketToCurrentUser;
  const {sub} = user.tokenData;
  return asyncPost({
    url: `/tickets`,
    title: 'tickets',
    body: {
      assignedTo:
        AssignNewTicketToCurrentUser.valueAsBool ||
        user.userRole === UserRoles.Staff
          ? sub
          : null,
      customerId: equipment.customerId,
      serviceLocationId: equipment.serviceLocationId,
      contactId: equipment.contactId,
      subject: equipment.name,
      equipment: equipmentItems.map(x => x.equipmentId),
    },
    dispatch,
  });
};

export const loadBarcode = equipmentId => dispatch => {
  try {
    return request({
      url: `/equipment/${equipmentId}/barcode`,
      raw: true,
    });
  } catch (error) {
    handleError(`Unable to load barcode`, error, dispatch);
  }
};

export const exportCsv = () => async (dispatch, getState) => {
  try {
    const {filterParams, params} = getParamsAndFilters(getState());
    const url = `/csv/equipment?${params}${filterParams || ''}`;

    const result = await request({url, headers: {contentType: 'text/csv'}});

    if (result.status === 200) {
      dispatch(alerts.success(result.data.message));
      return true;
    }

    return false;
  } catch (error) {
    handleError('Error exporting to CSV', error, dispatch);
    return false;
  }
};



// WEBPACK FOOTER //
// ./src/actions/equipmentActions.js